<script setup>
  import { Combobox, ComboboxButton, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue'

  const props = defineProps({
    employer: { type: Object }
  })

  const { user } = useUser()
  const supervisorEmployerCookie = useSupervisorEmployer()

  const selectedEmployer = ref(supervisorEmployerCookie.value ?? props.employer)
  const query = ref('')
  const filteredEmployers = computed(() => {
    if (!employers.data) return []
    return query.value === ''
      ? employers.data
      : employers.data.filter(employer => {
          return employer.name.toLowerCase().includes(query.value.toLowerCase())
        })
  })

  const employers = reactive({
    ...(await useLazyAsyncData(
      'supervisor-employers',
      async () => {
        return await findAll('employers', {
          fields: [
            'name',
            'slug',
            'subscriptionCredits',
            'subscriptionEnd',
            'subscriptionQuota',
            'subscriptionStart',
            'subscriptionType',
            'supportEmail',
            'visible'
          ],
          filters: {
            subscriptionType: { $not: null }
          },
          sort: ['visible:desc', 'name:asc'],
          populate: {
            type: { fields: ['slug'] },
            logo: { fields: ['url'] },
            subscriptionOptions: { fields: ['recruitmentRequest'] }
          }
        })
      },
      {
        server: false
      }
    ))
  })

  watch(
    selectedEmployer,
    async employer => {
      user.value.employments[0].employer = employer
      supervisorEmployerCookie.value = employer
      await navigateTo('/ats')
    },
    { immediate: true }
  )
</script>

<template>
  <p class="mb-1 pl-1 text-sm text-zinc-400">Vous êtes collaborateur fantôme</p>

  <Loading v-if="employers.loading" />
  <Combobox class="relative w-full" v-else v-model="selectedEmployer" as="div">
    <ComboboxInput class="form-input w-full" @change="query = $event.target.value" :display-value="employer => employer.name" />
    <ComboboxButton class="form-select absolute right-1 top-1 h-5/6 w-9 items-center border-0"></ComboboxButton>
    <ComboboxOptions
      class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
    >
      <ComboboxOption
        class="flex cursor-default items-center truncate px-2 py-1.5 text-sm hover:bg-zinc-100"
        v-for="employer in filteredEmployers"
        :key="employer.id"
        :value="employer"
      >
        <div
          class="mr-2 aspect-square h-5 w-5 rounded-full bg-zinc-300 bg-cover bg-center shadow"
          v-if="employer.logo?.url"
          :style="{
            backgroundImage: `url('${useStrapiMedia(employer.logo.url)}')`
          }"
        />
        <div class="mr-auto truncate" :class="{ 'text-zinc-400': !employer.visible }">{{ employer.name }}</div>
        <span class="ml-2 shrink-0 rounded bg-zinc-100 px-1 py-0.5 text-xs text-zinc-400" v-if="!employer.visible">inactif</span>
      </ComboboxOption>
    </ComboboxOptions>
  </Combobox>
</template>
